const formatBoolean = (value) => {
  if (value === true) return 'Enabled';
  if (value === false) return 'Disabled';
  return '—';
};

const safeValue = (value, formatter = (val) => val) => {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  return formatter(value);
};

const formatSeconds = (value) => `${value} seconds`;

const formatBaud = (value) => `${value} bps`;

const toNumber = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : '';
};

const toBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
  }
  if (typeof value === 'number') return value !== 0;
  return Boolean(value);
};

const baudRates = [1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200];

const BAUD_RATE_OPTIONS = baudRates.map((rate) => ({
  label: `${rate}`,
  value: String(rate),
}));

const timezoneOffsets = Array.from({ length: 25 }, (_, index) => index - 12);

const TIMEZONE_OPTIONS = timezoneOffsets.map((offset) => {
  const sign = offset >= 0 ? '+' : '';
  return {
    label: `GMT${sign}${offset}`,
    value: `GMT${sign}${offset}`,
  };
});

const deviceLabel = (device) =>
  device?.deviceName || device?.modelNumber || `PDU ${device?.enclosureSerialNumber || ''}`.trim();

const serialLabel = (device) => device?.enclosureSerialNumber || '—';

const configurationSections = [
  {
    key: 'general',
    label: 'General',
    icon: 'settings_applications',
    description: 'Device identity, reboot behaviour, and global AutoPing defaults.',
    listColumns: [
      {
        label: 'Device',
        value: (device) => deviceLabel(device) || '—',
      },
      {
        label: 'Serial Number',
        value: (device) => serialLabel(device),
      },
      {
        label: 'Global Reboot Time',
        value: (device) => safeValue(device?.globalRebootTime, (val) => `${val}s`),
      },
    ],
    detailFields: [
      {
        label: 'Device Name',
        value: ({ conf, device }) => conf?.deviceName || device?.deviceName || '—',
      },
      {
        label: 'Hostname',
        value: ({ conf, device }) =>
          conf?.hostName || conf?.hostname || device?.hostName || device?.hostname || '—',
      },
      {
        label: 'Global Outlet Reboot Time',
        value: ({ conf }) => safeValue(conf?.globalRebootTime, formatSeconds),
      },
      {
        label: 'AutoPing Interval',
        value: ({ conf }) => safeValue(conf?.autopingInterval, formatSeconds),
      },
      {
        label: 'AutoPing Timeout',
        value: ({ conf }) => safeValue(conf?.autopingTimeout, formatSeconds),
      },
    ],
    formSections: [
      {
        title: 'Device Settings',
        description: 'Adjust the device name and reboot cadence that apply to all outlets on this PDU. This reboot time is overriden by outlet specific custom reboot time.',
        fields: [
          {
            name: 'deviceName',
            label: 'Device Name',
            type: 'text',
            placeholder: 'Closet PDU Primary',
            getInitialValue: ({ conf, device }) => conf?.deviceName ?? device?.deviceName ?? '',
          },
          {
            name: 'globalRebootTime',
            label: 'Global Outlet Reboot Time (seconds)',
            type: 'number',
            min: 0,
            getInitialValue: ({ conf }) => toNumber(conf?.globalRebootTime),
            transformOut: (value) => (value === '' ? undefined : Number(value)),
          },
        ],
      },
      {
        title: 'AutoPing Defaults',
        description: 'Configure network heartbeat timing used by AutoPing rules.',
        fields: [
          {
            name: 'autopingInterval',
            label: 'Ping Interval (seconds)',
            type: 'number',
            min: 1,
            getInitialValue: ({ conf }) => toNumber(conf?.autopingInterval),
            transformOut: (value) => (value === '' ? undefined : Number(value)),
          },
          {
            name: 'autopingTimeout',
            label: 'Ping Timeout (seconds)',
            type: 'number',
            min: 1,
            getInitialValue: ({ conf }) => toNumber(conf?.autopingTimeout),
            transformOut: (value) => (value === '' ? undefined : Number(value)),
          },
        ],
      },
    ],
  },
  {
    key: 'smtp',
    label: 'SMTP',
    icon: 'mail',
    description: 'Email server used for outbound alert notifications originating from this device.',
    listColumns: [
      {
        label: 'Device',
        value: (device) => deviceLabel(device) || '—',
      },
      {
        label: 'Server',
        value: (device) => device?.smtpServer || '—',
      },
      {
        label: 'TLS',
        value: (device) => formatBoolean(device?.smtpTlsEnabled),
      },
    ],
    detailFields: [
      {
        label: 'SMTP Server',
        value: ({ conf }) => conf?.smtpServer || '—',
      },
      {
        label: 'Port',
        value: ({ conf }) => safeValue(conf?.smtpPort),
      },
      {
        label: 'TLS',
        value: ({ conf }) => formatBoolean(conf?.smtpTlsEnabled),
      },
      {
        label: 'Authentication Required',
        value: ({ conf }) => formatBoolean(conf?.smtpAuthRequired),
      },
      {
        label: 'Username',
        value: ({ conf }) => conf?.smtpUsername || '—',
      },
    ],
    formSections: [
      {
        title: 'General SMTP Settings',
        description: 'Provide mail server connection details for outbound notifications.',
        fields: [
          {
            name: 'smtpServer',
            label: 'SMTP Server Address',
            type: 'text',
            placeholder: 'smtp.example.com',
            getInitialValue: ({ conf }) => conf?.smtpServer || '',
          },
          {
            name: 'smtpPort',
            label: 'SMTP Port',
            type: 'number',
            min: 1,
            getInitialValue: ({ conf }) => toNumber(conf?.smtpPort),
            transformOut: (value) => (value === '' ? undefined : Number(value)),
          },
          {
            name: 'smtpTlsEnabled',
            label: 'Enable TLS',
            type: 'toggle',
            description: 'Encrypt email transport when the server supports TLS.',
            getInitialValue: ({ conf }) => toBoolean(conf?.smtpTlsEnabled),
          },
          {
            name: 'smtpAuthRequired',
            label: 'Authentication Required',
            type: 'toggle',
            description: 'Enable to require username and password when sending email.',
            getInitialValue: ({ conf }) => toBoolean(conf?.smtpAuthRequired),
          },
          {
            name: 'smtpUsername',
            label: 'SMTP Username',
            type: 'text',
            placeholder: 'user@example.com',
            getInitialValue: ({ conf }) => conf?.smtpUsername || '',
            disabled: (values) => !toBoolean(values.smtpAuthRequired),
          },
          {
            name: 'smtpPassword',
            label: 'SMTP Password',
            type: 'password',
            placeholder: 'Enter a new password',
            getInitialValue: () => '',
            disabled: (values) => !toBoolean(values.smtpAuthRequired),
            transformOut: (value) => (value === '' ? undefined : value),
          },
        ],
      },
    ],
  },
  {
    key: 'ntp',
    label: 'NTP & Time',
    icon: 'schedule',
    description: 'Time synchronisation hosts and timezone preferences.',
    listColumns: [
      {
        label: 'Device',
        value: (device) => deviceLabel(device) || '—',
      },
      {
        label: 'Server',
        value: (device) => device?.ntpHost || '—',
      },
      {
        label: 'NTP Enabled',
        value: (device) => formatBoolean(device?.ntpEnabled),
      },
    ],
    detailFields: [
      {
        label: 'NTP Enabled',
        value: ({ conf }) => formatBoolean(conf?.ntpEnabled),
      },
      {
        label: 'NTP Server',
        value: ({ conf }) => conf?.ntpHost || '—',
      },
      {
        label: 'Timezone',
        value: ({ conf }) => conf?.timeZone || conf?.timezone || '—',
      },
      {
        label: 'Daylight Savings',
        value: ({ conf }) => formatBoolean(conf?.daylightsavings ?? conf?.daylightSavings),
      },
    ],
    formSections: [
      {
        title: 'Network Time Protocol',
        description: 'Control synchronisation with a remote NTP server.',
        fields: [
          {
            name: 'ntpEnabled',
            label: 'Enable NTP Synchronisation',
            type: 'toggle',
            getInitialValue: ({ conf }) => toBoolean(conf?.ntpEnabled),
          },
          {
            name: 'ntpHost',
            label: 'NTP Server Address',
            type: 'text',
            placeholder: 'pool.ntp.org',
            getInitialValue: ({ conf }) => conf?.ntpHost || '',
            disabled: (values) => !toBoolean(values.ntpEnabled),
          },
        ],
      },
      {
        title: 'Time Preferences',
        description: 'Configure local timezone and daylight savings handling.',
        fields: [
          {
            name: 'daylightsavings',
            label: 'Daylight Savings Adjustment',
            type: 'toggle',
            getInitialValue: ({ conf }) => toBoolean(conf?.daylightsavings ?? conf?.daylightSavings),
          },
          {
            name: 'timeZone',
            label: 'Timezone',
            type: 'select',
            options: TIMEZONE_OPTIONS,
            getInitialValue: ({ conf }) => conf?.timeZone || conf?.timezone || '',
          },
        ],
      },
    ],
  },
  {
    key: 'syslog',
    label: 'Syslog',
    icon: 'list_alt',
    description: 'Remote logging targets for events and user activity.',
    listColumns: [
      {
        label: 'Device',
        value: (device) => deviceLabel(device) || '—',
      },
      {
        label: 'Server IP',
        value: (device) => device?.syslogServerIp || device?.syslogRemoteLogIp || '—',
      },
      {
        label: 'User Logging',
        value: (device) => formatBoolean(device?.enableRemoteUserLogging),
      },
    ],
    detailFields: [
      {
        label: 'Remote Server IP',
        value: ({ conf }) => conf?.syslogServerIp || conf?.syslogRemoteLogIp || '—',
      },
      {
        label: 'User Activity Logging',
        value: ({ conf }) => formatBoolean(conf?.enableRemoteUserLogging),
      },
      {
        label: 'Event Logging',
        value: ({ conf }) => formatBoolean(conf?.enableRemoteEventLogging),
      },
    ],
    formSections: [
      {
        title: 'Remote Syslog',
        description: 'Stream activity and event logs to an external syslog listener.',
        fields: [
          {
            name: 'syslogServerIp',
            label: 'Syslog Server IP',
            type: 'text',
            placeholder: '192.168.1.160',
            getInitialValue: ({ conf }) => conf?.syslogServerIp || conf?.syslogRemoteLogIp || '',
          },
          {
            name: 'enableRemoteUserLogging',
            label: 'Send User Activity Logs',
            type: 'toggle',
            getInitialValue: ({ conf }) => toBoolean(conf?.enableRemoteUserLogging),
          },
          {
            name: 'enableRemoteEventLogging',
            label: 'Send Event Logs',
            type: 'toggle',
            getInitialValue: ({ conf }) => toBoolean(conf?.enableRemoteEventLogging),
          },
        ],
      },
    ],
  },
  {
    key: 'ports',
    label: 'Ports',
    icon: 'usb',
    description: 'Serial and USB port behaviour for out-of-band access.',
    listColumns: [
      {
        label: 'Device',
        value: (device) => deviceLabel(device) || '—',
      },
      {
        label: 'Port A Mode',
        value: (device) => device?.portAMode || '—',
      },
      {
        label: 'USB FW Update',
        value: (device) => formatBoolean(device?.usbFwupdateControlEnabled),
      },
    ],
    detailFields: [
      {
        label: 'Port A Mode',
        value: ({ conf }) => conf?.portAMode || '—',
      },
      {
        label: 'Port A COM Baud',
        value: ({ conf }) => safeValue(conf?.portAComBaud, formatBaud),
      },
      {
        label: 'Port A Modem Baud',
        value: ({ conf }) => safeValue(conf?.portAModemBaud, formatBaud),
      },
      {
        label: 'USB COM Baud',
        value: ({ conf }) => safeValue(conf?.usbComBaud, formatBaud),
      },
      {
        label: 'USB Modem Baud',
        value: ({ conf }) => safeValue(conf?.usbModemBaud, formatBaud),
      },
      {
        label: 'USB Firmware Update Control',
        value: ({ conf }) => formatBoolean(conf?.usbFwupdateControlEnabled),
      },
    ],
    formSections: [
      {
        title: 'Port A (RJ-45)',
        description: 'Select the operating mode and baud rates for Port A.',
        fields: [
          {
            name: 'portAMode',
            label: 'Port A Mode',
            type: 'select',
            options: [
              { label: 'Sensor', value: 'sensor' },
              { label: 'COM (RS-232)', value: 'com' },
              { label: 'Modem (RS-232)', value: 'modem' },
            ],
            getInitialValue: ({ conf }) => conf?.portAMode || '',
          },
          {
            name: 'portAComBaud',
            label: 'COM Baud Rate',
            type: 'select',
            options: BAUD_RATE_OPTIONS,
            getInitialValue: ({ conf }) => (conf?.portAComBaud ? String(conf.portAComBaud) : ''),
            transformOut: (value) => (value === '' ? undefined : Number(value)),
            disabled: (values) => values.portAMode !== 'com',
          },
          {
            name: 'portAModemBaud',
            label: 'Modem Baud Rate',
            type: 'select',
            options: BAUD_RATE_OPTIONS,
            getInitialValue: ({ conf }) => (conf?.portAModemBaud ? String(conf.portAModemBaud) : ''),
            transformOut: (value) => (value === '' ? undefined : Number(value)),
            disabled: (values) => values.portAMode !== 'modem',
          },
        ],
      },
      {
        title: 'USB Serial',
        description: 'Configure baud rates and firmware permissions for USB access.',
        fields: [
          {
            name: 'usbComBaud',
            label: 'USB COM Baud Rate',
            type: 'select',
            options: BAUD_RATE_OPTIONS,
            getInitialValue: ({ conf }) => (conf?.usbComBaud ? String(conf.usbComBaud) : ''),
            transformOut: (value) => (value === '' ? undefined : Number(value)),
          },
          {
            name: 'usbModemBaud',
            label: 'USB Modem Baud Rate',
            type: 'select',
            options: BAUD_RATE_OPTIONS,
            getInitialValue: ({ conf }) => (conf?.usbModemBaud ? String(conf.usbModemBaud) : ''),
            transformOut: (value) => (value === '' ? undefined : Number(value)),
          },
          {
            name: 'usbFwupdateControlEnabled',
            label: 'Allow Firmware Update via USB',
            type: 'toggle',
            getInitialValue: ({ conf }) => toBoolean(conf?.usbFwupdateControlEnabled),
          },
        ],
      },
    ],
  },
  {
    key: 'users',
    label: 'Users',
    icon: 'group',
    description: 'Manage device-local accounts and role assignments.',
    listColumns: [
      {
        label: 'Device',
        value: (device) => deviceLabel(device) || '—',
      },
      {
        label: 'Local Users',
        value: (device) => {
          if (Array.isArray(device?.users)) {
            return device.users.length;
          }
          if (typeof device?.localUsersCount === 'number') {
            return device.localUsersCount;
          }
          return '—';
        },
      },
      {
        label: 'Roles',
        value: () => 'Manage per device',
      },
    ],
    detailFields: [],
    customComponent: 'DeviceUsers',
  },
];

export default configurationSections;
export { configurationSections, formatBoolean, safeValue };
